import { Component, AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {  MatListModule } from '@angular/material/list';
import { AuthUser } from '@app/app/store/auth/auth-user';
import { AuthState } from '@app/app/store/auth/auth.state';
import moment from 'moment';
import { DeleteNotification, PutNotificationRead, PutNotificationUnread } from '@app/app/store/notifications/notifications.actions';
import { NotificationsState } from '@app/app/store/notifications/notifications.state';
import { Notification } from '@app/app/interfaces/notification.model';

@Component({
  selector: 'grid-dynamic-task-cell',
  imports: [
    MatIconModule,
    CommonModule,
    MatListModule,
    MatMenuModule,
    MatTooltipModule
  ],
  templateUrl: './dynamic-cell.component.html'
})
export class DynamicCellComponent implements ICellRendererAngularComp, AfterViewInit, OnDestroy {
    @ViewChild(MatMenuTrigger) trigger!: MatMenuTrigger;

    public currentUser: AuthUser | undefined;
    public created?: string;
    public createdTimestamp: number;
    public mouseOver: boolean;
    public read: boolean;
    public params: ICellRendererParams;
    private rowElement!: HTMLElement;
    destroyed$ = new Subject<boolean>();
    notifications$: Observable<Notification[] | null> = this._store.select(NotificationsState.notifications);
    currentUser$: Observable<AuthUser | undefined> = this._store.select(AuthState.user);

    constructor(
      private _store: Store
    ) {
      this.currentUser$.pipe(takeUntilDestroyed()).subscribe(user => {
        this.currentUser = user;
      });
      this.notifications$.pipe(takeUntilDestroyed()).subscribe(notifications => {
        if (notifications) {
          const notification = notifications.find(n => n.activityId === this.params?.data.activityId);
          if (notification) {
            this.createdTimestamp = notification.createdTimestamp;
            this.created = this.formatTimestamp(this.createdTimestamp);
            this.read = !!notification.read;
          }
        }
      });
    }

    agInit(params: ICellRendererParams): void {
      this.params = params;
      this.createdTimestamp = this.params.data.createdTimestamp;
      this.created = this.formatTimestamp(this.createdTimestamp);
      this.read = !!this.params.data.read;
      this.mouseOver = false;
    }

    ngAfterViewInit() {
      // Get the row element after the view is initialized
      this.rowElement = this.params.eGridCell.parentElement as HTMLElement;

      // Add event listeners to the row element
      this.rowElement.addEventListener('mouseover', this.onRowMouseOver.bind(this));
      this.rowElement.addEventListener('mouseout', this.onRowMouseOut.bind(this));
    }

    /**
     * Ng lifecycle
     * @return void
     */
    ngOnDestroy(): void {
      this.destroyed$.next(true);
      this.destroyed$.complete();
    }

    value(): string {
      return this.params.value
    }

    formatTimestamp(dateStamp: number) {
        return moment.unix(dateStamp).fromNow()
    }

    refresh(params: ICellRendererParams) {
      return true;
    }

    onRowMouseOver() {
      this.mouseOver = true;
    }

    onRowMouseOut(event) {
      if (!this.rowElement.contains(event.relatedTarget)) {
        this.mouseOver = false;
        this.rowElement.classList.remove('ag-row-hover');
      }
    }

    isAssignmentMenuOpen() {
      return !!this.trigger?.menuOpen;
    }

    handleUnread(event) {
      event.stopPropagation();
      const { data } = this.params;
      const { activityId, read } = data;
    
      if (this.read) {
        this._store.dispatch(new PutNotificationUnread(activityId));

        this.read = false;
      }
    }

    handleRead(event) {
      event.stopPropagation();
      const { data } = this.params;
      const { activityId, read } = data;
    
      if (!this.read) {
        this._store.dispatch(new PutNotificationRead(activityId));

        this.read = true;
      }
    }

    handleDelete(event) {
      event.stopPropagation();
      const { data } = this.params;
      const { activityId } = data;
      this._store.dispatch(new DeleteNotification(activityId));
    }

    cellClickHandler(event) {
      event.stopPropagation();
    }
}
